import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BriefcaseBusiness, ClipboardCheck, FileSearch, FileUp, PlayCircle, X } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import AppSidebar from "@/components/AppSidebar";
import { ProviderTabBar } from "@/components/provider/ProviderShell";
import { HandoffBanner } from "@/components/provider/HandoffBanner";
import { EditorModal } from "@/components/EditorModal";
import {
  ProviderInvoicesPanel,
  ProviderReportingPanel,
  SupervisionNotesPanel,
} from "@/components/ProviderPortalControls";
import { WeeklySpNotesPanel } from "@/components/WeeklySpNotesPanel";
import { ProgressNotesPanel } from "@/components/provider/ProgressNotesPanel";
import { ProviderConfirmationsPanel } from "@/components/ProviderConfirmationsPanel";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const formatDay = (value) => (value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

const queueSections = [
  { key: "placements", title: "My placements", icon: BriefcaseBusiness, endpoint: "/provider-placements", empty: "No placements have been assigned yet." },
  { key: "evaluation_requests", title: "Evaluations and screenings", icon: FileSearch, endpoint: "/testing-requests", empty: "No evaluations or screenings have been assigned yet." },
  { key: "client_tasks", title: "Client tasks", icon: ClipboardCheck, endpoint: "/client-tasks", empty: "No client tasks have been assigned yet." },
];

const WORK_STATUS_LABELS = { "Report Submitted": "Pending TAG review" };
const openCount = (items = []) => items.filter((item) => !["Completed", "Closed"].includes(item.status)).length;

export default function ProviderPortalPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "system_administrator";
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState("");
  const [quickAction, setQuickAction] = useState("");

  async function load() {
    try {
      const response = await api.get("/providers/me/work-queue");
      setQueue(response.data);
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function advance(item, endpoint) {
    const next = item.status === "Assigned" ? "Acknowledged" : item.status === "Acknowledged" ? "In Progress" : "Completed";
    try {
      await api.post(`${endpoint}/${item.id}/status`, { status: next });
      await load();
    } catch (requestError) {
      setError(formatApiError(requestError));
    }
  }

  const is1099 = queue?.provider?.engagement_type === "1099";

  return (
    <div className={`app-shell ${isAdmin ? "" : "provider-with-tabs"}`} data-testid="provider-portal-shell">
      <AppSidebar activeItem={isAdmin ? "My caseload" : "Provider portal"} />
      <div className="app-content">
        <AppHeader />
        <main className="dashboard-page provider-portal-page" data-testid="provider-portal-page">
          <div className="page-intro">
            <div>
              <p className="eyebrow">MY WORK QUEUE</p>
              <h1>{isAdmin ? "My caseload" : "My work"}</h1>
              <p className="page-subtitle">Your placements, evaluation requests, and client-directed work in one secure workspace.</p>
            </div>
          </div>
          {error && <p className="form-message error-message" data-testid="provider-portal-error">{error}</p>}
          {!queue && !error && <p className="projection-loading" data-testid="provider-portal-loading">Loading your work queue…</p>}
          {queue && (
            <>
              <section className="provider-welcome" data-testid="provider-welcome"><p className="eyebrow">YOUR WORKSPACE</p><h1>Welcome back, {queue.provider.first_name}</h1><p>Here’s what’s happening in your workspace.</p></section>
              <HandoffBanner handoffs={queue.handoffs} onChanged={load} onError={setError} />
              <section className="provider-summary-grid" data-testid="provider-work-summary">
                <button type="button" data-testid="provider-summary-placements" onClick={() => document.querySelector('[data-testid="provider-placements-panel"]')?.scrollIntoView({ behavior: "smooth" })}><BriefcaseBusiness size={18} /><strong>{openCount(queue.placements)}</strong><span>My placements · open</span></button>
                {queue.features.evaluation_requests_enabled && (
                  <button type="button" data-testid="provider-summary-evaluations" onClick={() => document.querySelector('[data-testid="provider-evaluation_requests-panel"]')?.scrollIntoView({ behavior: "smooth" })}><FileSearch size={18} /><strong>{openCount(queue.evaluation_requests)}</strong><span>Evaluations & screenings · open</span></button>
                )}
                <button type="button" data-testid="provider-summary-tasks" onClick={() => document.querySelector('[data-testid="provider-client_tasks-panel"]')?.scrollIntoView({ behavior: "smooth" })}><ClipboardCheck size={18} /><strong>{openCount(queue.client_tasks)}</strong><span>Client tasks · open</span></button>
              </section>
              <ProviderConfirmationsPanel />
              <section className="provider-queue-grid">
                {queueSections
                  .filter((section) => section.key !== "evaluation_requests" || queue.features.evaluation_requests_enabled)
                  .map((section) => (
                    <QueueSection key={section.key} section={section} items={queue[section.key]} advance={advance} onReload={load} onError={setError} />
                  ))}
              </section>
              <section className="provider-quick-actions" data-testid="provider-quick-actions">
                <h2>Quick Actions</h2>
                {queue.features.progress_reports_enabled && <button data-testid="quick-progress-report" type="button" onClick={() => setQuickAction("notes")}>Submit progress notes</button>}
                {queue.features.time_reports_enabled && <button data-testid="quick-time-report" type="button" onClick={() => setQuickAction("progress")}>Submit time report</button>}
                <button data-testid="quick-sp-notes" type="button" onClick={() => setQuickAction("sp-notes")}>Submit Weekly SP Notes PDF</button>
                {is1099 && queue.features.invoice_submissions_enabled && (
                  <>
                    <button data-testid="quick-create-invoice" type="button" onClick={() => setQuickAction("invoice")}>Create Invoice</button>
                    <button data-testid="quick-upload-invoice" type="button" onClick={() => setQuickAction("invoice")}>Upload Invoice</button>
                  </>
                )}
              </section>
              {quickAction === "sp-notes" && <WeeklySpNotesPanel placements={queue.placements || []} />}
              {quickAction === "notes" && <ProgressNotesPanel />}
              {quickAction === "progress" && <ProviderReportingPanel queue={queue} onReload={load} />}
              {quickAction === "invoice" && is1099 && <ProviderInvoicesPanel queue={queue} onReload={load} />}
              {(isAdmin || queue.provider?.supervision_applies !== false) && <SupervisionNotesPanel queue={queue} onReload={load} />}
            </>
          )}
        </main>
      </div>
      {!isAdmin && <ProviderTabBar />}
    </div>
  );
}

function PlacementDetail({ item, onClose }) {
  return (
    <EditorModal onClose={onClose} testId="placement-detail-modal">
      <section className="inline-edit-panel placement-detail" data-testid="placement-detail">
        <div className="section-heading">
          <div>
            <p className="eyebrow">PLACEMENT</p>
            <h2>{item.title}</h2>
            <p>{item.client_name}{item.evaluation_type ? ` · ${item.evaluation_type}` : ""}</p>
          </div>
          <button className="projection-icon-button" data-testid="close-placement-detail" type="button" onClick={onClose}><X size={16} /></button>
        </div>
        <dl className="placement-detail-grid">
          <div><dt>Status</dt><dd data-testid="placement-detail-status">{item.status}</dd></div>
          <div><dt>Start</dt><dd>{formatDay(item.start_date)}</dd></div>
          <div><dt>End</dt><dd>{item.end_date ? formatDay(item.end_date) : "Ongoing"}</dd></div>
          <div><dt>Due</dt><dd>{item.due_date ? formatDay(item.due_date) : "—"}</dd></div>
        </dl>
        {item.description && <p className="placement-detail-description" data-testid="placement-detail-description">{item.description}</p>}
        {item.status_history?.length > 0 && (
          <ol className="provider-work-timeline" data-testid="placement-detail-timeline">
            {item.status_history.map((entry) => (
              <li key={`${entry.status}-${entry.occurred_at}`}>{entry.status} · {new Date(entry.occurred_at).toLocaleDateString()}</li>
            ))}
          </ol>
        )}
      </section>
    </EditorModal>
  );
}

function EvaluationReportUpload({ item, onDone, onError }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  async function upload(event) {
    event.preventDefault();
    if (!file) {
      onError("Choose the evaluation report (PDF or DOCX) before submitting.");
      return;
    }
    setBusy(true);
    try {
      const data = new FormData();
      data.append("report_file", file);
      await api.post(`/testing-requests/${item.id}/report`, data, { headers: { "Content-Type": "multipart/form-data" } });
      await onDone();
    } catch (requestError) {
      onError(formatApiError(requestError));
      setBusy(false);
    }
  }

  return (
    <form className="evaluation-report-upload" data-testid={`evaluation-report-upload-${item.id}`} onSubmit={upload}>
      <label>
        {(item.service_kind || "Evaluation") === "Other" ? "Service" : item.service_kind || "Evaluation"} report (PDF or DOCX)
        <input accept="application/pdf,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/*" data-testid={`evaluation-report-file-${item.id}`} type="file" onChange={(event) => setFile(event.target.files?.[0] || null)} required />
      </label>
      <button className="button button-primary" data-testid={`submit-evaluation-report-${item.id}`} disabled={busy} type="submit"><FileUp size={14} /> {busy ? "Uploading…" : "Submit report for TAG review"}</button>
    </form>
  );
}

function QueueSection({ section, items, advance, onReload, onError }) {
  const Icon = section.icon;
  const [detail, setDetail] = useState(null);
  const isEvaluation = section.key === "evaluation_requests";
  const open = items.filter((item) => item.status !== "Completed").length;
  return (
    <section className="provider-queue-panel" data-testid={`provider-${section.key}-panel`}>
      {detail && <PlacementDetail item={detail} onClose={() => setDetail(null)} />}
      <div className="provider-queue-heading">
        <Icon size={18} />
        <div>
          <h2>{section.title}</h2>
          <p>{open} active item{open === 1 ? "" : "s"}</p>
        </div>
      </div>
      {items.map((item) => (
        <article className="provider-queue-item" key={item.id} data-testid={`provider-work-${item.id}`}>
          <strong>{item.title}</strong>
          <span>{item.client_name}{item.service_label ? ` · ${item.service_label}` : item.evaluation_type ? ` · ${item.evaluation_type}` : ""}</span>
          {section.key === "evaluation_requests" && <Link className="provider-open-link" data-testid={`provider-open-${item.id}`} to={`/provider-portal/work/${item.id}`}>Open {(item.service_kind || "evaluation").toLowerCase() === "other" ? "service" : (item.service_kind || "evaluation").toLowerCase()} ›</Link>}
          {item.description && <p>{item.description}</p>}
          <small>
            {item.due_date ? `Due ${formatDay(item.due_date)}` : item.start_date ? `${formatDay(item.start_date)} — ${item.end_date ? formatDay(item.end_date) : "ongoing"}` : "No due date set"}
          </small>
          <div>
            <em data-testid={`provider-work-status-${item.id}`}>{isEvaluation && item.status === "In Progress" && item.return_comment ? "Returned for revisions" : WORK_STATUS_LABELS[item.status] || item.status}</em>
            {section.key !== "placements" && ["Assigned", "Acknowledged"].includes(item.status) && (
              <button className="button button-secondary" data-testid={`advance-provider-work-${item.id}`} type="button" onClick={() => advance(item, section.endpoint)}>
                <PlayCircle size={14} /> {item.status === "Assigned" ? "Acknowledge" : "Start"}
              </button>
            )}
            {!isEvaluation && section.key !== "placements" && item.status === "In Progress" && (
              <button className="button button-secondary" data-testid={`advance-provider-work-${item.id}`} type="button" onClick={() => advance(item, section.endpoint)}>
                <PlayCircle size={14} /> Mark complete
              </button>
            )}
            {section.key === "placements" && <button className="button button-secondary" data-testid={`view-provider-work-${item.id}`} type="button" onClick={() => setDetail(item)}>View Placement</button>}
          </div>
          {isEvaluation && item.status === "In Progress" && item.return_comment && (
            <p className="evaluation-awaiting-client" data-testid={`evaluation-return-comment-${item.id}`}>Returned by {item.returned_by_name || "TAG"}: {item.return_comment}</p>
          )}
          {isEvaluation && item.status === "In Progress" && <EvaluationReportUpload item={item} onDone={onReload} onError={onError} />}
          {isEvaluation && item.report && (
            <p className="evaluation-report-submitted" data-testid={`evaluation-report-submitted-${item.id}`}>
              Report {item.report.original_filename} submitted {formatDay(item.report.uploaded_at)} · {item.status === "Completed" ? "TAG review complete" : item.status === "In Progress" && item.return_comment ? "returned for revisions — upload a revised report" : "awaiting TAG review"}
            </p>
          )}
          {isEvaluation && item.your_rate != null && (
            <p className="evaluation-your-rate" data-testid={`evaluation-your-rate-${item.id}`}>Your rate: ${Number(item.your_rate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          )}
          {isEvaluation && item.billing_cycle && (
            <p className={`evaluation-cycle-notice ${item.billing_cycle.moved_to_next_cycle ? "is-moved" : ""}`} data-testid={`evaluation-cycle-notice-${item.id}`}>{item.billing_cycle.notice}</p>
          )}
          {item.status_history?.length > 0 && (
            <ol className="provider-work-timeline" data-testid={`provider-work-timeline-${item.id}`}>
              {item.status_history.map((entry) => (
                <li key={`${entry.status}-${entry.occurred_at}`}>{WORK_STATUS_LABELS[entry.status] || entry.status} · {new Date(entry.occurred_at).toLocaleDateString()}</li>
              ))}
            </ol>
          )}
        </article>
      ))}
      {!items.length && <p className="provider-empty-state" data-testid={`provider-${section.key}-empty`}>{section.empty}</p>}
    </section>
  );
}
